import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Users, Activity, FlaskConical, BarChart3, LineChart as LineIcon, TrendingUp } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar,
} from "recharts";
import { toast } from "sonner";

const DOMAIN_COLORS = [
  "#2f6f5c", "#c66a4a", "#7a4d9c", "#3e7ea8", "#a4823a",
  "#b04a6a", "#4a8a5c", "#a5583a", "#5a6faa", "#8c6ca8",
  "#3f7d7d",
];

/**
 * Módulo Investigación — cohort export, longitudinal analysis & group comparison.
 * Roles: admin, researcher, professional.
 */
export default function Research() {
  const [cohorts, setCohorts] = useState([]);
  const [selected, setSelected] = useState("all");
  const [longitudinal, setLongitudinal] = useState(null);
  const [compare, setCompare] = useState(null);
  const [prePost, setPrePost] = useState(null);

  useEffect(() => {
    api.get("/research/cohorts").then((r) => setCohorts(r.data)).catch(() => toast.error("No se pudieron cargar las cohortes"));
    api.get("/research/compare").then((r) => setCompare(r.data)).catch(() => {});
    api.get("/research/pre-post").then((r) => setPrePost(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const q = selected && selected !== "all" ? `?institution=${encodeURIComponent(selected)}` : "";
    api.get(`/research/longitudinal${q}`).then((r) => setLongitudinal(r.data)).catch(() => {});
  }, [selected]);

  const totals = useMemo(() => {
    const totalPatients = cohorts.reduce((s, c) => s + c.patients, 0);
    const totalAttempts = cohorts.reduce((s, c) => s + c.attempts, 0);
    return { totalPatients, totalAttempts, totalInstitutions: cohorts.length };
  }, [cohorts]);

  const downloadCsv = async () => {
    try {
      const q = selected && selected !== "all" ? `?institution=${encodeURIComponent(selected)}` : "";
      const token = localStorage.getItem("sc_access_token");
      const res = await fetch(`${API}/research/export.csv${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cohort_${selected || "todas"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch {
      toast.error("No se pudo descargar el CSV");
    }
  };

  const compareRows = useMemo(() => {
    if (!compare) return [];
    return compare.domains.map((dom, i) => {
      const row = { domain: dom.replace(/_/g, " "), _color: DOMAIN_COLORS[i % DOMAIN_COLORS.length] };
      compare.institutions.forEach((inst) => {
        const v = compare.matrix?.[inst]?.[dom];
        row[inst] = v === null || v === undefined ? null : Math.round(v * 100);
      });
      return row;
    });
  }, [compare]);

  return (
    <div className="space-y-8" data-testid="research-page">
      <div>
        <p className="text-sm uppercase tracking-widest text-primary font-semibold">Investigación</p>
        <h1 className="heading text-4xl sm:text-5xl font-extrabold mt-1 tracking-tight">Cohortes y análisis</h1>
        <p className="text-lg text-foreground/70 mt-2 max-w-2xl">
          Exportación longitudinal y comparativa entre entidades. Ideal para estudios clínicos
          multicéntricos y análisis en SPSS/R/Python.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard testid="metric-institutions" icon={<FlaskConical size={22} />} label="Entidades" value={totals.totalInstitutions} />
        <MetricCard testid="metric-patients" icon={<Users size={22} />} label="Participantes" value={totals.totalPatients} />
        <MetricCard testid="metric-attempts" icon={<Activity size={22} />} label="Intentos totales" value={totals.totalAttempts} />
      </div>

      {/* Cohort selector + export */}
      <Card className="rounded-2xl border-border">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="heading font-bold text-lg">Cohorte a analizar</div>
              <p className="text-sm text-foreground/70">Filtra por entidad para el gráfico longitudinal y la exportación.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                data-testid="cohort-select"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="h-11 px-4 rounded-full border border-border bg-background text-sm"
              >
                <option value="all">Todas las entidades</option>
                {cohorts.map((c) => (
                  <option key={c.institution} value={c.institution}>
                    {c.institution} · {c.patients} participantes
                  </option>
                ))}
              </select>
              <Button
                className="rounded-full h-11 px-5"
                onClick={downloadCsv}
                data-testid="download-cohort-csv"
              >
                <Download size={16} className="mr-2" /> Exportar CSV
              </Button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {cohorts.map((c) => (
              <Badge
                key={c.institution}
                variant={selected === c.institution ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => setSelected(c.institution)}
                data-testid={`cohort-badge-${slug(c.institution)}`}
              >
                {c.institution} · {c.patients}/{c.attempts}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Longitudinal chart */}
      <Card className="rounded-2xl border-border" data-testid="longitudinal-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2 text-primary">
            <LineIcon size={18} /> <span className="heading font-bold text-lg">Evolución semanal por dominio</span>
          </div>
          <p className="text-sm text-foreground/70 mb-4">
            {longitudinal?.patient_count ?? 0} participantes · acierto medio (%) por semana ISO.
          </p>
          {longitudinal && longitudinal.weeks.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={longitudinal.weeks.map((w) => {
                const row = { week: w.week };
                longitudinal.domains.forEach((d) => {
                  row[d] = w[d] === null || w[d] === undefined ? null : Math.round(w[d] * 100);
                });
                return row;
              })}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="week" fontSize={12} />
                <YAxis domain={[0, 100]} fontSize={12} unit="%" />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {longitudinal.domains.map((d, i) => (
                  <Line
                    key={d}
                    type="monotone"
                    dataKey={d}
                    stroke={DOMAIN_COLORS[i % DOMAIN_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-foreground/60">Aún no hay suficientes datos para esta cohorte.</p>
          )}
        </CardContent>
      </Card>

      {/* Group comparison */}
      <Card className="rounded-2xl border-border" data-testid="compare-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2 text-accent">
            <BarChart3 size={18} /> <span className="heading font-bold text-lg">Comparación entre entidades</span>
          </div>
          <p className="text-sm text-foreground/70 mb-4">
            Acierto medio (%) por dominio en cada entidad. Compara pacientes de distintos centros.
          </p>
          {compare && compare.institutions.length > 0 && compareRows.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(240, compareRows.length * 44)}>
              <BarChart data={compareRows} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis type="number" domain={[0, 100]} unit="%" fontSize={12} />
                <YAxis type="category" dataKey="domain" width={160} fontSize={12} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {compare.institutions.map((inst, i) => (
                  <Bar
                    key={inst}
                    dataKey={inst}
                    fill={DOMAIN_COLORS[i % DOMAIN_COLORS.length]}
                    radius={[0, 6, 6, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-foreground/60">Aún no hay al menos dos entidades con datos suficientes.</p>
          )}
        </CardContent>
      </Card>

      {/* PRE → POST clinical impact */}
      <Card className="rounded-2xl border-border" data-testid="prepost-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2 text-primary">
            <TrendingUp size={18} /> <span className="heading font-bold text-lg">Impacto clínico: PRE → POST</span>
          </div>
          <p className="text-sm text-foreground/70 mb-4">
            Mejora media (puntos porcentuales) de la evaluación PRE a la POST por entidad, solo sobre
            participantes con ambas evaluaciones finalizadas.
          </p>

          {prePost && prePost.institutions.length > 0 ? (
            <>
              {/* Overall improvement per institution */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                {prePost.institutions.map((inst) => (
                  <div
                    key={inst.institution}
                    className={`rounded-2xl border p-4 ${inst.overall.delta > 0 ? "border-primary/40 bg-primary/5" : "border-destructive/40 bg-destructive/5"}`}
                    data-testid={`prepost-inst-${slug(inst.institution)}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="heading font-bold text-base leading-tight">{inst.institution}</div>
                      <Badge variant="outline" className="shrink-0">n={inst.n_paired}</Badge>
                    </div>
                    <div className="mt-3 flex items-baseline gap-2">
                      <span className={`heading text-3xl font-extrabold ${inst.overall.delta > 0 ? "text-primary" : "text-destructive"}`}>
                        {inst.overall.delta_pct_points > 0 ? "+" : ""}{inst.overall.delta_pct_points} pp
                      </span>
                      <span className="text-xs text-foreground/70">mejora media</span>
                    </div>
                    <div className="mt-2 text-xs text-foreground/70">
                      PRE <b>{Math.round(inst.overall.pre * 100)}%</b> → POST <b>{Math.round(inst.overall.post * 100)}%</b>
                    </div>
                  </div>
                ))}
              </div>

              {/* Grouped bar: PRE vs POST per institution */}
              <ResponsiveContainer width="100%" height={Math.max(220, prePost.institutions.length * 60)}>
                <BarChart
                  data={prePost.institutions.map((i) => ({
                    institution: i.institution,
                    PRE: Math.round(i.overall.pre * 100),
                    POST: Math.round(i.overall.post * 100),
                    Delta: Math.round(i.overall.delta_pct_points),
                  }))}
                  layout="vertical"
                  margin={{ left: 20, right: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis type="number" domain={[0, 100]} unit="%" fontSize={12} />
                  <YAxis type="category" dataKey="institution" width={200} fontSize={12} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="PRE" fill="#a5583a" radius={[0, 6, 6, 0]} />
                  <Bar dataKey="POST" fill="#2f6f5c" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>

              {/* Per-domain delta heatmap-style table */}
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">
                  Mejora por dominio (puntos porcentuales)
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse" data-testid="prepost-domain-table">
                    <thead>
                      <tr className="text-left">
                        <th className="p-2 border-b border-border font-semibold">Entidad</th>
                        {prePost.all_domains.map((d) => (
                          <th key={d} className="p-2 border-b border-border font-semibold capitalize whitespace-nowrap">
                            {d.replace(/_/g, " ")}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {prePost.institutions.map((inst) => {
                        const byDom = Object.fromEntries((inst.domains || []).map((d) => [d.domain, d]));
                        return (
                          <tr key={inst.institution}>
                            <td className="p-2 border-b border-border/60 font-medium">{inst.institution}</td>
                            {prePost.all_domains.map((d) => {
                              const cell = byDom[d];
                              const pp = cell?.delta_pct_points;
                              const bg = deltaBg(pp);
                              return (
                                <td
                                  key={d}
                                  className={`p-2 border-b border-border/60 text-center ${bg}`}
                                  title={cell ? `PRE ${Math.round(cell.pre*100)}% → POST ${Math.round(cell.post*100)}% (n=${cell.n})` : "sin datos"}
                                >
                                  {pp == null ? "—" : `${pp > 0 ? "+" : ""}${pp}`}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-foreground/60">
              Aún no hay participantes con las evaluaciones PRE y POST finalizadas.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function deltaBg(pp) {
  if (pp == null) return "text-foreground/40";
  if (pp >= 25) return "bg-primary/30 text-primary font-bold";
  if (pp >= 15) return "bg-primary/20 text-primary font-semibold";
  if (pp >= 5) return "bg-primary/10 text-primary";
  if (pp <= -5) return "bg-destructive/15 text-destructive";
  return "text-foreground/70";
}

function MetricCard({ icon, label, value, testid }) {
  return (
    <Card className="rounded-2xl border-border" data-testid={testid}>
      <CardContent className="p-5 flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-primary/15 text-primary grid place-items-center">{icon}</div>
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
          <div className="heading text-3xl font-extrabold">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function slug(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
